export const VIRIDIS_PALETTE = [
  "#440154", "#482878", "#3e4989", "#31688e", "#26828e",
  "#1f9e89", "#35b779", "#6ece58", "#b5de2b", "#fde725"
];

export const CAGR_ORDER = ["aktiva", "trzby", "ebitda", "zasoby", "zadluzenost"];

export const CAGR_LABELS = {
  aktiva: "Růst Aktiv (CAGR)",
  trzby: "Růst Tržeb (CAGR)",
  ebitda: "Růst EBITDA (CAGR)",
  zasoby: "Růst Zásob (CAGR)",
  zadluzenost: "Změna Zadluženosti (CAGR)"
};

export const CAGR_WINDOWS = [
  { label: "2018–2024 (6 let)", value: 6 },
  { label: "2022–2025 (3 roky)", value: 3 }
];

export const INDICATORS = [
  "ROA", "ROE", "ROS", "EBITDA marže", "Provozní marže",
  "Likvidita běžná", "Likvidita pohotová", "Likvidita okamžitá",
  "Zadluženost celková", "Zadluženost dlouhodobá",
  "Obrat aktiv", "Obrat zásob",
  "Doba obratu pohledávek", "Doba obratu závazků", "Cyklus peněz",
  "Marže přidané hodnoty", "Osobní náklady / přidaná hodnota", "Podíl osobních nákladů",
  "Meziroční růst tržeb"
];

export const PERCENT_METRICS = [
  "ROA", "ROE", "ROS",
  "EBITDA marže", "Provozní marže",
  "Zadluženost celková", "Zadluženost dlouhodobá",
  "Marže přidané hodnoty", "Osobní náklady / přidaná hodnota", "Podíl osobních nákladů", "Meziroční růst tržeb",
  "aktiva", "trzby", "ebitda", "zasoby", "zadluzenost"
];

export const INTEGER_METRICS = [
  "Obrat aktiv", "Obrat zásob", "Doba obratu pohledávek", "Doba obratu závazků", "Cyklus peněz"
];

export const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const formatValue = (value, indicatorName) => {
  if (!Number.isFinite(value)) return "—";

  if (PERCENT_METRICS.includes(indicatorName)) {
      return (value * 100).toFixed(1) + "%";
  }
  if (INTEGER_METRICS.includes(indicatorName)) {
      return value.toFixed(0);
  }
  return value.toFixed(2);
};

export const getColorByRank = (rank, total) => {
  if (total <= 1) return VIRIDIS_PALETTE[5];
  const index = Math.floor((rank / (total - 1)) * (VIRIDIS_PALETTE.length - 1));
  return VIRIDIS_PALETTE[index] || VIRIDIS_PALETTE[VIRIDIS_PALETTE.length - 1];
};

export const deduplicateData = (data, context) => {
  if (!data || data.length === 0) return [];

  let codeKey = "Sekce";
  if (context === "lvl1") codeKey = "1 úroveň";
  if (context === "lvl2" || context === "single") codeKey = "2 úroveň ID";

  const groups = {};

  data.forEach(item => {
    let rawCode = item[codeKey];
    let normalizedKey = String(rawCode);
    const asNum = parseFloat(rawCode);
    if (!isNaN(asNum) && String(rawCode).includes('.')) {
      normalizedKey = String(asNum);
    }

    if (!groups[normalizedKey]) {
      groups[normalizedKey] = item;
    } else {
      const existingN = safeNum(groups[normalizedKey].n) || 0;
      const currentN = safeNum(item.n) || 0;

      if (currentN > existingN) {
        groups[normalizedKey] = item;
      }
    }
  });

  return Object.values(groups);
};

export const buildGradientBarOption = (title, data, refValue, indicatorName) => {
  const total = data.length;

  return {
    title: { show: false },
    tooltip: {
        trigger: "axis",
        formatter: (params) => {
            const i = params[0].dataIndex;
            const item = data[i];
            return `<b>${item.code}</b><br/>${item.name}<br/>Medián: <b>${formatValue(item.value, indicatorName)}</b>`;
        }
    },
    grid: { left: 40, right: 20, top: 30, bottom: 40, containLabel: true },
    xAxis: {
        type: "category",
        data: data.map(d => d.code),
        axisLabel: { interval: 0, rotate: 0, fontWeight: 'bold' }
    },
    yAxis: {
        type: "value",
        splitLine: { lineStyle: { color: "#eee" } },
        axisLabel: {
            formatter: (val) => PERCENT_METRICS.includes(indicatorName) ? (val * 100).toFixed(0) + "%" : val
        }
    },
    series: [{
        type: "bar",
        data: data.map((d, idx) => ({
            value: d.value,
            itemStyle: {
                color: getColorByRank(total - 1 - idx, total),
                borderRadius: [3, 3, 0, 0]
            }
        })),
        barMaxWidth: 60,
        markLine: refValue ? {
            data: [{ yAxis: refValue, name: 'Celkem' }],
            lineStyle: { color: "#c1121f", width: 2, type: "dashed" },
            label: { show: true, position: "end", formatter: `Celkem: ${formatValue(refValue, indicatorName)}` },
            symbol: ['none', 'none']
        } : undefined
    }]
  };
};

export const buildBoxplotOption = (title, data, indicatorName) => ({
  title: { show: false },
  tooltip: {
      trigger: "item",
      confine: true,
      formatter: (param) => {
          const item = data[param.dataIndex];
          const s = item.stats;
          return `<b>${item.code}</b> - ${item.name}<br/>
                  Max (p90): ${formatValue(s[4], indicatorName)}<br/>
                  Q3: ${formatValue(s[3], indicatorName)}<br/>
                  Median: ${formatValue(s[2], indicatorName)}<br/>
                  Q1: ${formatValue(s[1], indicatorName)}<br/>
                  Min (p10): ${formatValue(s[0], indicatorName)}`;
      }
  },
  grid: { left: 40, right: 20, bottom: 60, top: 30, containLabel: true },
  xAxis: {
      type: "category",
      data: data.map(d => d.code),
      axisLabel: { rotate: 45, interval: 0 }
  },
  yAxis: { type: "value", splitLine: { lineStyle: { color: "#eee" } } },
  series: [{
      name: "boxplot",
      type: "boxplot",
      data: data.map(d => d.stats),
      itemStyle: { color: "#b6cdc6", borderColor: "#027360", borderWidth: 1.5 },
      emphasis: { itemStyle: { borderWidth: 2, shadowBlur: 5 } }
  }]
});

export const determineContext = (selectedSekce, selectedL1, selectedL2) => {
  if (selectedSekce === "Vše") return "sections";
  if (selectedL1 === "Vše") return "lvl1";
  if (selectedL2 === "Vše") return "lvl2";
  return "single";
};

export const prepareBarData = (benchmarkData, selectedIndicator, currentContext) => {
  if (!benchmarkData.length) return [];
  const col = `${selectedIndicator}__median`;

  const items = benchmarkData
    .map(r => {
        let code = r.Sekce;
        let name = r["Sekce název"];

        if (currentContext === "lvl1") {
            code = r["1 úroveň"];
            name = r["1 úroveň název"];
        } else if (currentContext === "lvl2" || currentContext === "single") {
            code = r["2 úroveň ID"];
            name = r["2 úroveň název"];
        }

        return {
            code: String(code),
            name: name,
            value: safeNum(r[col])
        };
    })
    .filter(d => d.value !== null)
    .sort((a, b) => b.value - a.value);

  return items;
};

export const prepareBoxData = (benchmarkData, selectedIndicator, currentContext) => {
  if (!benchmarkData.length) return [];
  const items = [];
  const k = selectedIndicator;

  benchmarkData.forEach(r => {
      const p10 = safeNum(r[`${k}__p10`]);
      const q1  = safeNum(r[`${k}__q1`]);
      const med = safeNum(r[`${k}__median`]);
      const q3  = safeNum(r[`${k}__q3`]);
      const p90 = safeNum(r[`${k}__p90`]);

      if ([p10, q1, med, q3, p90].every(v => v !== null)) {
            let code = r.Sekce;
            let name = r["Sekce název"];
            if (currentContext === "lvl1") { code = r["1 úroveň"]; name = r["1 úroveň název"]; }
            else if (currentContext.includes("lvl2") || currentContext === "single") { code = r["2 úroveň ID"]; name = r["2 úroveň název"]; }

            items.push({
                code: String(code),
                name: name,
                stats: [p10, q1, med, q3, p90],
                median: med
            });
      }
  });
  return items.sort((a,b) => b.median - a.median);
};

export const calculateReferenceValue = (barData) => {
  if (!barData.length) return null;
  const values = barData.map(d => d.value).sort((a,b) => a-b);
  const mid = Math.floor(values.length / 2);
  return values.length % 2 !== 0 ? values[mid] : (values[mid - 1] + values[mid]) / 2;
};

export const determineCagrContext = (cagrSekce, cagrL1) => {
  if (cagrSekce === "Vše") return "root";
  if (cagrL1 === "Vše") return "sekce";
  return "l1";
};

export const getCagrTitle = (cagrContext, cagrSekce) => {
  if (cagrContext === "root") return "Všechny sekce";
  return `Sekce ${cagrSekce}`;
};

export const prepareCagrBarData = (cagrData, selectedCagrMetric, cagrWindowYears, cagrContext, allSections, allL1, allL2) => {
  if (!cagrData.length) return [];

  return cagrData
    .map(item => {
      const metricObj = item.metrics?.find(
        m => m.metric === selectedCagrMetric && m.years === cagrWindowYears
      );

      if (!metricObj) return null;

      const code = String(item.id);
      let name = code;

      if (cagrContext === "root") {
        const s = allSections.find(x => String(x.Sekce) === code);
        if (s) name = s["Sekce název"];
      }
      else if (cagrContext === "sekce") {
        const l1 = allL1.find(x => String(x["1 úroveň"]) === code);
        if (l1) name = l1["1 úroveň název"];
      }
      else {
        const l2 = allL2.find(x => String(x["2 úroveň ID"]) === code);
        if (l2) name = l2["2 úroveň název"];
      }

      return {
        code,
        name,
        value: safeNum(metricObj.cagr_median)
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.value - a.value);
};
